import { defineStore } from 'pinia';
import { ref } from 'vue';

import type {
  SymbolDTO,
  SymbolPackDeleteResultDTO,
  SymbolPackDTO,
  SymbolPackUpdateDTO,
  SymbolUpdateDTO,
} from '@nora-monorepo/nora-api-client';
import { useApiStore } from '@nora-monorepo/ui-shared';

import { router } from '#nora-ui/router';
import { navigateToRoute } from '#nora-ui/router/navigation';
import { RouteName } from '#nora-ui/router/route-names';

export const useAdminSymbolPackStore = defineStore('adminSymbolPackStore', () => {
  const api = useApiStore();

  const isCreatingSymbolPack = ref(false);
  const isEditingSymbolPack = ref(false);
  const isLoadingSymbolPacks = ref(false);
  const symbolPacks = ref<SymbolPackDTO[]>([]);

  function setIsCreatingSymbolPack(value: boolean): void {
    isCreatingSymbolPack.value = value;
  }

  function setIsEditingSymbolPack(value: boolean): void {
    isEditingSymbolPack.value = value;
  }

  async function loadSymbolPacksData(): Promise<void> {
    try {
      isLoadingSymbolPacks.value = true;

      const response = await api.unwrap(
        api.symbol.listSymbolPacks({}),
      );

      if (response.data) {
        symbolPacks.value = response.data;
      }
    }
    catch (error) {
      console.error('Failed to load symbol packs:', error);
    }
    finally {
      isLoadingSymbolPacks.value = false;
    }
  }

  async function loadSymbolPackDetails(packId: string): Promise<SymbolPackDTO> {
    const response = await api.unwrap(
      api.symbol.getSymbolPack({ packId }),
    );

    if (!response.data) {
      throw new Error('Symbol pack details were not found');
    }

    return response.data;
  }

  async function updateSymbolPack(packId: string, updateData: SymbolPackUpdateDTO): Promise<SymbolPackDTO | undefined> {
    try {
      const response = await api.unwrap(
        api.symbol.updateSymbolPack({ packId, symbolPackUpdateDTO: updateData }),
      );

      if (response.data) {
        const index = symbolPacks.value.findIndex(pack => pack.id === packId);
        if (index !== -1) {
          symbolPacks.value[index] = response.data;
        }

        return response.data;
      }
    }
    catch (error) {
      console.error('Failed to update symbol pack:', error);
    }

    return undefined;
  }

  async function deleteSymbolPack(packId: string): Promise<boolean> {
    try {
      await api.unwrap(
        api.symbol.deleteSymbolPack({ packId }),
      );

      symbolPacks.value = symbolPacks.value.filter(pack => pack.id !== packId);
      closeEditorForDeletedPack(packId);

      return true;
    }
    catch (error) {
      console.error('Failed to delete symbol pack:', error);
    }

    return false;
  }

  async function addSymbolToPack(packId: string, file: File): Promise<SymbolDTO | undefined> {
    try {
      const response = await api.unwrap(
        api.symbol.addSymbolToPack({ packId, file }),
      );

      return response.data;
    }
    catch (error) {
      console.error('Failed to add symbol to pack:', error);
    }

    return undefined;
  }

  async function deleteSymbol(packId: string, symbolId: string): Promise<boolean> {
    try {
      await api.unwrap(
        api.symbol.deleteSymbol({ packId, symbolId }),
      );

      return true;
    }
    catch (error) {
      console.error('Failed to delete symbol:', error);
    }

    return false;
  }

  async function updateSymbol(
    packId: string,
    symbolId: string,
    updateData: SymbolUpdateDTO,
  ): Promise<SymbolDTO | undefined> {
    try {
      const response = await api.unwrap(
        api.symbol.updateSymbol({
          packId,
          symbolId,
          symbolUpdateDTO: updateData,
        }),
      );

      return response.data;
    }
    catch (error) {
      console.error('Failed to update symbol:', error);
    }

    return undefined;
  }

  function eventCreateSymbolPack(symbolPack: SymbolPackDTO): void {
    const existingIndex = symbolPacks.value.findIndex(pack => pack.id === symbolPack.id);
    if (existingIndex === -1) {
      symbolPacks.value.push(symbolPack);
    }
  }

  function eventUpdateSymbolPack(symbolPack: SymbolPackDTO): void {
    const index = symbolPacks.value.findIndex(pack => pack.id === symbolPack.id);
    if (index !== -1) {
      symbolPacks.value[index] = symbolPack;
    }
  }

  function closeEditorForDeletedPack(packId: string): void {
    const currentRoute = router.currentRoute.value;
    const { symbolPackId } = currentRoute.params;

    if (currentRoute.name === RouteName.AdminSymbolPacks && symbolPackId === packId) {
      isEditingSymbolPack.value = false;
      navigateToRoute(RouteName.AdminSymbolPacks);
    }
  }

  function eventDeleteSymbolPack(deleteResult: SymbolPackDeleteResultDTO): void {
    const packId = deleteResult.id;
    symbolPacks.value = symbolPacks.value.filter(pack => pack.id !== packId);
    closeEditorForDeletedPack(packId);
  }

  function reset(): void {
    isCreatingSymbolPack.value = false;
    isEditingSymbolPack.value = false;
    isLoadingSymbolPacks.value = false;
    symbolPacks.value = [];
  }

  return {
    isCreatingSymbolPack,
    isEditingSymbolPack,
    isLoadingSymbolPacks,
    symbolPacks,

    addSymbolToPack,
    deleteSymbol,
    deleteSymbolPack,
    eventCreateSymbolPack,
    eventDeleteSymbolPack,
    eventUpdateSymbolPack,
    loadSymbolPackDetails,
    loadSymbolPacksData,
    reset,
    setIsEditingSymbolPack,
    setIsCreatingSymbolPack,
    updateSymbol,
    updateSymbolPack,
  };
});
